import { motion } from "framer-motion";
import { LOGO_URL } from "../../lib/projects";

/* ------------------------------------------------------------------
   Unified outro — ex-AboutSection + ex-Footer merged.

   Layout:
     ┌──────────────────────────────────────────────────────────┐
     │ — ABOUT US                                               │
     │ Paragraph about the studio                               │
     │ (second line)                                            │
     │                                                          │
     │                                                          │
     │ ┌──────┐   CONTACT   FOLLOW    STUDIO                    │
     │ │ LOGO │   hello@    IG                                  │
     │ │      │   work@     VM        → THE STUDIO              │
     │ └──────┘             YT        Post.  VFX.  Madness.     │
     ├──────────────────────────────────────────────────────────┤
     │ © year · crafted in the dark                             │
     └──────────────────────────────────────────────────────────┘

   • All bottom-row items share a baseline (items-end).
   • Paragraph up top reads as a closing studio statement.
   • Meta strip sits in its own thin bordered row at the base.
   ------------------------------------------------------------------ */

const SOCIALS = [
    { label: "Instagram", href: "https://instagram.com", handle: "@madhousestudios" },
    { label: "Vimeo", href: "https://vimeo.com", handle: "/madhouse" },
    { label: "YouTube", href: "https://youtube.com", handle: "@madhousestudios" }
];

// Per-char hover lift — `whitespace-nowrap` ensures the label never wraps
// one-letter-per-line inside a narrow flex-column parent.
const LinkLabel = ({ text }) => (
    <span className="relative inline-flex whitespace-nowrap">
        {Array.from(text).map((ch, i) => (
            <motion.span
                key={`${text}-${i}`}
                whileHover={{ y: -3, transition: { duration: 0.25 } }}
                className="inline-block"
                style={{ transitionDelay: `${i * 10}ms` }}
            >
                {ch === " " ? "\u00A0" : ch}
            </motion.span>
        ))}
    </span>
);

export const Footer = () => {
    const year = new Date().getFullYear();

    return (
        <footer
            id="contact"
            data-testid="site-footer"
            className="relative border-t border-ink-border bg-ink px-6 pb-6 pt-16 md:px-12 md:pb-8 md:pt-24"
        >
            <div className="mx-auto flex max-w-[1800px] flex-col gap-16 md:gap-28">
                {/* ── ABOUT US ─ closing studio statement ──────────────── */}
                <AboutBlock />

                {/* ── BOTTOM-ALIGNED ROW · logo + meta columns ─────────── */}
                <div className="flex flex-col gap-10 md:flex-row md:items-end md:justify-between md:gap-16">
                    {/* Logo — left with a gentle inward offset so it doesn't
                        hug the page edge and reads as part of the centered
                        composition. Slightly larger than before to match the
                        visual weight of the meta columns. */}
                    <motion.img
                        src={LOGO_URL}
                        alt="Mad House Studios"
                        draggable={false}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        data-testid="outro-wordmark"
                        className="h-auto w-auto max-w-[170px] select-none md:ml-24 md:max-w-[200px] lg:ml-40"
                    />

                    {/* Right-hand meta cluster — two evenly-spaced columns */}
                    <div className="flex flex-col gap-10 sm:flex-row sm:gap-16 md:gap-20">
                        {/* Contact */}
                        <OutroColumn testId="outro-col-contact" heading="Contact">
                            <a
                                href="mailto:hello@madhousestudios.com"
                                data-cursor="hover"
                                data-testid="outro-email-hello"
                                className="hover:text-fog-primary"
                            >
                                hello@madhousestudios.com
                            </a>
                            <a
                                href="mailto:work@madhousestudios.com"
                                data-cursor="hover"
                                data-testid="outro-email-work"
                                className="hover:text-fog-primary"
                            >
                                work@madhousestudios.com
                            </a>
                        </OutroColumn>

                        {/* Follow */}
                        <OutroColumn testId="outro-col-social" heading="Follow">
                            {SOCIALS.map((s) => (
                                <a
                                    key={s.label}
                                    href={s.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`social-${s.label.toLowerCase()}`}
                                    data-cursor="hover"
                                    className="group inline-flex items-baseline gap-2 whitespace-nowrap hover:text-fog-primary"
                                >
                                    <LinkLabel text={s.label} />
                                    <span className="text-[9px] italic tracking-[0.18em] text-fog-muted opacity-0 transition-opacity duration-300 group-hover:opacity-70">
                                        {s.handle}
                                    </span>
                                </a>
                            ))}
                        </OutroColumn>
                    </div>
                </div>

                {/* ── Meta strip ─────────────────────────────────────────── */}
                <div className="flex flex-wrap items-center justify-between gap-4 border-t border-ink-border pt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-fog-muted md:text-xs">
                    <span>© {year} Mad House Studios</span>
                    <span className="flex items-center gap-3">
                        <span className="italic">Crafted in the dark</span>
                        <span className="inline-block h-[5px] w-[5px] rotate-45 bg-[#DB162F]" />
                    </span>
                </div>
            </div>
        </footer>
    );
};

/* ------------------------------------------------------------------
   AboutBlock — closing studio statement. Editorial eyebrow + two
   short lines. Left-aligned, maxed to a readable column width so
   the paragraph reads as copy, not a banner.
   ------------------------------------------------------------------ */
const AboutBlock = () => (
    <div
        data-testid="outro-about"
        className="mx-auto max-w-[780px] text-center md:max-w-[900px]"
    >
        <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto max-w-[720px] font-sans text-sm leading-relaxed text-fog-primary md:text-base"
        >
            A post-production crew with a{" "}
            <span className="italic text-[#DB162F]">strict eye for detail.</span>{" "}
            Crafted visuals. Controlled execution. Frames worth keeping.
        </motion.p>

        <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mx-auto mt-4 max-w-[680px] font-sans text-sm leading-relaxed text-fog-secondary md:text-base"
        >
            We are an award-winning studio building commercials, brand films, music
            videos and cinematic CGI worlds — shaped in the edit room, coloured in
            the dark, and finished with care.
        </motion.p>
    </div>
);

/* ------------------------------------------------------------------
   OutroColumn — shared layout for the metadata stacks.
   ------------------------------------------------------------------ */
const OutroColumn = ({ testId, heading, children, className = "" }) => (
    <div
        data-testid={testId}
        className={`flex flex-col gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-fog-secondary md:text-xs ${className}`}
    >
        <span className="flex items-center gap-2 text-[9px] tracking-[0.4em] text-fog-muted md:text-[10px]">
            <span className="inline-block h-[1px] w-4 bg-fog-muted/60" />
            {heading}
        </span>
        <div className="flex flex-col gap-2.5 leading-relaxed transition-colors duration-300">
            {children}
        </div>
    </div>
);

export default Footer;
